import { Asset } from '../../../models/image/dto/asset';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderDto } from '../../../models/image/dto/media-manager-folder-dto';

/**
 * Look up a single asset in the asset library by its unique identifier (id-md5Hash).
 * Returns null when the asset is not present.
 */
export function findAssetInLibrary(assetId: string, assetLibrary: MediaManagerAssetLibraryDto): Asset | null {
  return assetLibrary.assets.find(asset => asset.getUniqueIdentifier() === assetId) ?? null;
}

/**
 * Look up multiple assets in the asset library. Returns null if any single lookup fails so callers can bail out
 * before applying a partial patch.
 */
export function findAssetsInLibrary(assets: Asset[], assetLibrary: MediaManagerAssetLibraryDto): Asset[] | null {
  const canonicalAssets: Asset[] = [];
  for (const asset of assets) {
    const canonicalAsset = findAssetInLibrary(asset.getUniqueIdentifier(), assetLibrary);
    if (!canonicalAsset) {
      return null;
    }
    canonicalAssets.push(canonicalAsset);
  }
  return canonicalAssets;
}

/** Look up a single folder in the asset library by id. Returns null when the folder is not present. */
export function findFolderInLibrary(
  folderId: string,
  assetLibrary: MediaManagerAssetLibraryDto
): MediaManagerFolderDto | null {
  return assetLibrary.assetFolders.find(folder => folder.id === folderId) ?? null;
}

/** Look up multiple folders in the asset library. Returns null if any single lookup fails. */
export function findFoldersInLibrary(
  folders: MediaManagerFolderDto[],
  assetLibrary: MediaManagerAssetLibraryDto
): MediaManagerFolderDto[] | null {
  const canonicalFolders: MediaManagerFolderDto[] = [];
  for (const folder of folders) {
    const canonicalFolder = findFolderInLibrary(folder.id, assetLibrary);
    if (!canonicalFolder) {
      return null;
    }
    canonicalFolders.push(canonicalFolder);
  }
  return canonicalFolders;
}
